import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORY_ICONS } from "../lib/constants";
import { MapPin, Calendar, Clock, MessageCircle, CheckCircle2, X, Star } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { useT } from "../i18n/I18nContext";

function Stars({ value, onChange, size = 24, testidPrefix = "star" }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange?.(n)}
          data-testid={`${testidPrefix}-${n}`}
          className={`transition-transform ${onChange ? "hover:scale-110" : ""}`}
          aria-label={`${n} stars`}
        >
          <Star
            className={`${n <= value ? "fill-current text-mabw-accent" : "text-stone-300"}`}
            style={{ width: size, height: size }}
          />
        </button>
      ))}
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const [req, setReq] = useState(null);
  const [offers, setOffers] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [offerMessage, setOfferMessage] = useState("");
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingDone, setRatingDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setReq(data);
      if (user && data.requester_id === user.id) {
        const { data: off } = await api.get(`/requests/${id}/offers`);
        setOffers(off);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="max-w-3xl mx-auto px-6 py-16 text-red-600" data-testid="rd-error">{error}</div>;
  if (!req) return <div className="max-w-3xl mx-auto px-6 py-16 text-mabw-soft" data-testid="rd-loading">{t("common.loading")}</div>;

  const Icon = CATEGORY_ICONS[req.category] || CATEGORY_ICONS.other;
  const isRequester = user && req.requester_id === user.id;
  const isHelper = user && req.helper_id === user.id;

  const offerHelp = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/requests/${req.id}/offer`, { message: offerMessage });
      setOfferMessage("");
      await load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const acceptOffer = async (helperId) => {
    setBusy(true); setError("");
    try { await api.post(`/requests/${req.id}/accept/${helperId}`); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const completeReq = async () => {
    setBusy(true);
    try { await api.post(`/requests/${req.id}/complete`); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const cancelReq = async () => {
    setBusy(true);
    try { await api.post(`/requests/${req.id}/cancel`); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submitRating = async () => {
    if (!ratingStars) return;
    setBusy(true);
    const rated = isRequester ? req.helper_id : req.requester_id;
    try {
      await api.post(`/ratings`, {
        request_id: req.id, rated_user_id: rated,
        stars: ratingStars, comment: ratingComment,
      });
      setRatingDone(true);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-14 anim-fade-up" data-testid="request-detail-page">
      <button onClick={() => navigate(-1)} className="btn-mabw-ghost mb-6 -ml-3" data-testid="rd-back">
        {t("common.back")}
      </button>

      <div className="card-mabw p-6 md:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-stone-50 flex items-center justify-center text-mabw-secondary border border-mabw">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-mabw-secondary font-semibold">
                {t(`categories.${req.category}.label`)}
              </div>
              <h1 className="text-3xl font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>{req.title}</h1>
            </div>
          </div>
          <StatusBadge status={req.status} testid="rd-status" />
        </div>

        <p className="mt-6 text-mabw-text leading-relaxed whitespace-pre-line">{req.description}</p>

        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-mabw-soft">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {req.location}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {req.date}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {req.estimated_duration}</span>
        </div>

        <div className="mt-8 pt-6 border-t border-mabw flex items-center justify-between text-sm">
          <div className="text-mabw-soft">
            {t("detail.requested_by")}{" "}
            <Link to={`/app/utenti/${req.requester_id}`} className="text-mabw-text font-medium hover:text-mabw-primary" data-testid="rd-requester-link">
              {req.requester?.first_name} {req.requester?.last_initial}
            </Link>{" "}
            · {req.requester?.city}
          </div>
          {req.requester?.ratings_count > 0 && (
            <div>★ {req.requester.avg_rating.toFixed(1)} <span className="text-mabw-soft">({req.requester.ratings_count})</span></div>
          )}
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="rd-error-inline">{error}</div>
        )}

        {user && !isRequester && req.status === "open" && (
          <div className="mt-8 pt-6 border-t border-mabw">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>{t("detail.offer_title")}</h3>
            <textarea
              rows={3}
              value={offerMessage}
              onChange={(e) => setOfferMessage(e.target.value)}
              placeholder={t("detail.offer_placeholder")}
              className="input-mabw mt-3 resize-none"
              data-testid="rd-offer-message"
            />
            <button onClick={offerHelp} disabled={busy} className="btn-mabw mt-3" data-testid="rd-offer-btn">
              {busy ? t("detail.sending") : t("detail.offer_btn")}
            </button>
          </div>
        )}

        {isRequester && req.status === "open" && (
          <div className="mt-8 pt-6 border-t border-mabw">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("detail.offers_received")} ({offers.length})
            </h3>
            {offers.length === 0 ? (
              <p className="text-sm text-mabw-soft mt-2">{t("detail.no_offers")}</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {offers.map((o) => (
                  <li key={o.id} className="border border-mabw rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`offer-${o.id}`}>
                    <div>
                      <div className="font-medium">{o.helper.first_name} {o.helper.last_initial} · {o.helper.city}</div>
                      <div className="text-sm text-mabw-soft mt-1">{o.message || t("detail.offered_to_help")}</div>
                      {o.helper.ratings_count > 0 && (
                        <div className="text-xs text-mabw-soft mt-1">★ {o.helper.avg_rating.toFixed(1)} ({o.helper.ratings_count} {t("common.reviewsShort")})</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/app/messaggi/${o.helper.id}`} className="btn-mabw-secondary" data-testid={`offer-msg-${o.id}`}>
                        <MessageCircle className="w-4 h-4" /> {t("detail.message")}
                      </Link>
                      <button onClick={() => acceptOffer(o.helper.id)} disabled={busy} className="btn-mabw" data-testid={`offer-accept-${o.id}`}>
                        {t("detail.accept")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={cancelReq} className="btn-mabw-ghost mt-5 text-red-600" data-testid="rd-cancel-btn">
              <X className="w-4 h-4" /> {t("detail.cancel_request")}
            </button>
          </div>
        )}

        {req.status === "accepted" && req.helper && (
          <div className="mt-8 pt-6 border-t border-mabw">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("detail.in_progress_with", { name: `${req.helper.first_name} ${req.helper.last_initial}` })}
            </h3>
            <p className="text-sm text-mabw-soft mt-1">{t("detail.coordinate")}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={`/app/messaggi/${isRequester ? req.helper.id : req.requester_id}`} className="btn-mabw-secondary" data-testid="rd-msg-counterpart">
                <MessageCircle className="w-4 h-4" /> {t("detail.open_chat")}
              </Link>
              {isRequester && (
                <button onClick={completeReq} disabled={busy} className="btn-mabw" data-testid="rd-complete-btn">
                  <CheckCircle2 className="w-4 h-4" /> {t("detail.mark_done")}
                </button>
              )}
            </div>
          </div>
        )}

        {req.status === "completed" && (isRequester || isHelper) && (
          <div className="mt-8 pt-6 border-t border-mabw">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("detail.rate_user", { name: isRequester ? req.helper?.first_name : req.requester?.first_name })}
            </h3>
            {ratingDone ? (
              <p className="text-sm text-mabw-secondary mt-2" data-testid="rd-rating-done">{t("detail.rating_thanks")}</p>
            ) : (
              <div className="mt-3 space-y-3">
                <Stars value={ratingStars} onChange={setRatingStars} testidPrefix="rd-star" />
                <textarea
                  rows={3}
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder={t("detail.rating_placeholder")}
                  className="input-mabw resize-none"
                  data-testid="rd-rating-comment"
                />
                <button onClick={submitRating} disabled={busy || !ratingStars} className="btn-mabw" data-testid="rd-rating-submit">
                  {t("detail.rating_submit")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
